













































































import {
  computed,
  defineComponent,
  reactive,
  watch,
  ref,
} from '@vue/composition-api';
import { useFormat, useRouter } from '@ideavivat/vivat-2';
import { useMutation, useQuery, useResult } from '@vue/apollo-composable';

import generateColorPalette from '@/generateColorPalette';

import CommonChip from '@/components/CommonChip.vue';
import FlexCompanyMetric from '@/components/Company/FlexCompanyMetric.vue';
import PanelWithBranchInformation from '@/components/Company/PanelWithBranchInformation.vue';
import CommonFormWithCompanyInformationEditor from '@/components/Company/CommonFormWithCompanyInformationEditor.vue';
import CommonSelectWithCompanyDriverPayout from '@/components/Setting/CommonSelectWithCompanyDriverPayout.vue';
import CompanyColorsInput from '@/components/Setting/CompanyColorsInput.vue';
import CommonModalWithPreset from '@/components/CommonModalWithPreset.vue';

import getMyCompany from '@/gql/company/getMyCompany.gql';
import uploadLogo from '@/gql/company/uploadLogo.gql';
import uploadBg from '@/gql/company/uploadBg.gql';
import updateCompanyColors from '@/gql/company/updateCompanyColors.gql';

const Company = defineComponent({
  components: {
    CommonChip,
    FlexCompanyMetric,
    PanelWithBranchInformation,
    CommonFormWithCompanyInformationEditor,
    CommonSelectWithCompanyDriverPayout,
    CompanyColorsInput,
    CommonModalWithPreset,
  },
  setup() {
    const { route } = useRouter();
    function toBytes(val: any) { return useFormat.bytes(val); }
    function toDate(val: any) { return useFormat.date(val).displayFullThaiDate(); }
    const doneModal = ref(null);
    const company = reactive({
      id: -1,
      name: '',
      subdomain: '',
      package: '',
      userType: '',
      remarks: '',
      startDate: '',
      expireDate: '',
      maxStorage: '',
      taxId: '',
      billingAddress: '',
      province: { nameInThai: '' },
      district: { nameInThai: '' },
      subdistrict: { nameInThai: '' },
      zipcode: '',
      contactName: '',
      contactPhone: '',
      usersCount: '',
      vehiclesCount: '',
      driversCount: '',
      hubs: {
        id: '',
        name: '',
        code: '',
        hubType: '',
      },
      logoUrl: '',
      backgroundUrl: '',
      colors: [
        { key: 'primary', colorCode: '#174681' },
        { key: 'secondary', colorCode: '#f9ae1b' },
      ],
    });
    const colorIds = ref([]);

    const { result: getCompanyResult, loading } = useQuery(getMyCompany);
    const companyResult = useResult(getCompanyResult, { id: -1 }, (data) => data.me.company);
    watch(() => companyResult.value, (newVal, oldVal) => {
      if (!loading.value && newVal !== oldVal) {
        company.id = parseInt(companyResult.value.id, 10);
        company.name = companyResult.value.name;
        company.subdomain = companyResult.value.subdomain;
        company.package = companyResult.value.package;
        company.userType = companyResult.value.userType;
        company.remarks = companyResult.value.remarks;
        company.startDate = companyResult.value.startDate;
        company.expireDate = companyResult.value.expireDate;
        company.maxStorage = companyResult.value.maxStorage;
        company.taxId = companyResult.value.taxId;
        company.billingAddress = companyResult.value.billingAddress;
        company.province = companyResult.value.address.province || { nameInThai: '' };
        company.district = companyResult.value.address.district || { nameInThai: '' };
        company.subdistrict = companyResult.value.address.subdistrict || { nameInThai: '' };
        company.zipcode = companyResult.value.address.zipcode;
        company.contactName = companyResult.value.contactName;
        company.contactPhone = companyResult.value.contactPhone;
        company.usersCount = companyResult.value.usersCount;
        company.vehiclesCount = companyResult.value.vehiclesCount;
        company.driversCount = companyResult.value.driversCount;
        company.hubs = companyResult.value.hubs;

        company.logoUrl = companyResult.value.logoUrl;
        company.backgroundUrl = companyResult.value.backgroundUrl;

        company.colors = companyResult.value.colors;
        colorIds.value = companyResult.value.colors.map((color: any) => color.id);
      }
    });

    const computedAddress = computed(() => {
      if (company.id > -1) {
        return `${company.billingAddress ? company.billingAddress : ''}
        ${company.province?.nameInThai ? company.province.nameInThai : ''}
        ${company.district?.nameInThai ? company.district.nameInThai : ''}
        ${company.subdistrict?.nameInThai ? company.subdistrict.nameInThai : ''}`;
      }
      return '';
    });

    const refetchQueries = ['getMe', 'getMyInfo', 'getMyCompany'];
    const uploadLogoMutation = useMutation(uploadLogo, {
      refetchQueries,
    });
    uploadLogoMutation.onDone(() => (doneModal.value as any).compose({ entity: ' Logo ', action: 'บันทึก' }));
    const uploadBgMutation = useMutation(uploadBg, {
      refetchQueries,
    });
    uploadBgMutation.onDone(() => (doneModal.value as any).compose({ entity: ' Background ', action: 'บันทึก' }));
    const uploadCompanyFileVariables = computed(() => {
      if (company.id !== null) return { id: company.id };
      return {};
    });

    /* ---- Update Company Colors ---- */
    // const primaryColor = computed(() => colorSearch('primary-500'));
    // const secondaryColor = computed(() => colorSearch('secondary-500'));
    const computedColorsInput = computed(() => ([
      { ...company.colors.find((c) => c.key === 'primary'), __typename: undefined },
      { ...company.colors.find((c) => c.key === 'secondary'), __typename: undefined },
    ]));

    const computedCompanyInput = computed(() => {
      const result = {
        colors: {
          create: [...computedColorsInput.value],
          delete: colorIds.value,
        },
      };

      function buildPalette(name: string, color500: string) {
        const colorResult: { key: string; colorCode: string }[] = [];
        const palette = generateColorPalette(color500);
        colorResult.push({ key: name, colorCode: color500 });
        Object.entries(palette.colors).forEach(([key, val]) => {
          colorResult.push({ key: `${name}-${key}`, colorCode: val });
        });
        return colorResult;
      }

      const primary = (result.colors.create as { key: string; colorCode: string }[]).find((c) => c.key === 'primary');
      const secondary = (result.colors.create as { key: string; colorCode: string }[]).find((c) => c.key === 'secondary');

      result.colors.create = [
        ...buildPalette('primary', (primary) ? primary.colorCode : '#00D190') as any,
        ...buildPalette('secondary', (secondary) ? secondary.colorCode : '#1F60C6') as any,
      ];

      return result;
    });
    const updateCompanyColorsMutation = useMutation(updateCompanyColors, () => ({
      variables: {
        input: {
          id: company.id,
          ...computedCompanyInput.value,
        },
      },
      refetchQueries,
    }));
    updateCompanyColorsMutation.onDone(() => {
      (doneModal.value as any).compose({
        entity: ' Theme ระบบ',
        action: 'บันทึก',
      });
    });

    function colorSearch(name: string) {
      if ('colors' in company) {
        const color = ((company as any).colors as { key: string; colorCode: string }[]).find((c) => c.key === name);
        if (color) return color.colorCode;
      }
      return '#174681';
    }

    return {
      // _company,
      computedCompanyInput,
      updateCompanyColorsMutation,
      doneModal,

      company,
      toBytes,
      toDate,
      // primaryColor,
      // secondaryColor,
      computedAddress,

      uploadLogoMutation,
      uploadBgMutation,
      uploadCompanyFileVariables,
    };
  },
});

export default Company;
